/**
 * User settings API client methods.
 *
 * Handles user profile and settings management.
 */

import { API_CONFIG } from '../config/api';
import { getToken } from './tokenStorage';
import { logApiRequest, logApiResponse, logApiError } from '../utils/errorHandler';
import { toRequestError } from '../utils/apiError';
import type { TokenResponse } from './authApi';

/** Alert-worthiness toggles (T26): which briefing alerts the user wants. */
export interface AlertPrefs {
  stop: boolean;
  target: boolean;
  catalyst: boolean;
}

export interface UserSettings {
  id: number;
  username: string;
  email: string;
  email_verified: boolean;
  base_currency: string;
  prefer_language: string;
  answer_style: string;
  /** Null = never configured = all alerts on. */
  alert_prefs?: AlertPrefs | null;
}

export interface UpdateUserSettings {
  base_currency?: string;
  prefer_language?: string;
  answer_style?: string;
  /** Partial map — the backend merges it over the stored prefs. */
  alert_prefs?: Partial<AlertPrefs>;
}

/**
 * Get current user settings.
 */
export async function getUserSettings(): Promise<UserSettings> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/users/me`;
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: 'Failed to get user settings' }));
      const error = new Error(errorData.detail || 'Failed to get user settings');
      logApiError(method, url, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, url, error, duration);
    throw error;
  }
}

/**
 * Update user settings.
 */
export async function updateUserSettings(settings: UpdateUserSettings): Promise<UserSettings> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/users/me/settings`;
  const method = 'PUT';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url, settings);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(settings),
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: 'Failed to update settings' }));
      const error = new Error(errorData.detail || 'Failed to update settings');
      logApiError(method, url, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, url, error, duration);
    throw error;
  }
}

export interface EmailChangeRequest {
  new_email: string;
  current_password: string;
}

/** 202 body from POST /users/me/email-change. The account is untouched. */
export interface EmailChangeAcceptedResponse {
  status: 'pending_email_change';
  masked_new_email: string;
  resend_available_in_seconds: number;
}

/**
 * Request an email change. The backend emails a code to the NEW address and
 * returns 202 with the masked address; nothing on the account changes until
 * confirmEmailChange redeems the code.
 *
 * The body carries current_password, so it is deliberately NOT passed to
 * logApiRequest — that helper console.logs its third argument under __DEV__, and
 * a password must never reach the logs even in development. The request-side log
 * is therefore called with the method and URL only.
 */
export async function requestEmailChange(
  data: EmailChangeRequest
): Promise<EmailChangeAcceptedResponse> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/users/me/email-change`;
  const method = 'POST';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    // No third argument: the body holds current_password. See the docstring.
    logApiRequest(method, url);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const error = await toRequestError(response);
      logApiError(method, url, { code: error.code, status: error.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, url, error, duration);
    throw error;
  }
}

/**
 * Redeem the emailed code and swap the address. Returns a fresh TokenResponse
 * because the backend bumps token_version on the change; the caller must store
 * the new token or this device's session dies on the next request.
 *
 * The body is a one-time code, not a secret, but it is still not passed to
 * logApiRequest, for consistency with requestEmailChange.
 */
export async function confirmEmailChange(code: string): Promise<TokenResponse> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/users/me/email-change/confirm`;
  const method = 'POST';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ code }),
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const error = await toRequestError(response);
      logApiError(method, url, { code: error.code, status: error.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, url, error, duration);
    throw error;
  }
}
